(ns practitest.frontend.components2.dashboard.charts.scales
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.axis :as axis]))

(defn- scale-kind
  [{:keys [type scale]}]
  (or (:type scale) type))

(defn- d3-scale [kind]
  (case kind
      :temporal           (.scaleUtc d3)
      :quantitative       (.scaleLinear d3)
      :point              (.scalePoint d3)
      (:ordinal :nominal) (.scaleBand d3)))

(defn- field-values [field data]
  (cond
    (map? data)    (mapcat #(map field %) (vals data))
    (vector? data) (map field data)))

(defn- domain
  "Returns the input range a scale maps from.

   Honors an explicit (:domain scale) when provided. Otherwise pulls all
   `field` values out of the data and reduces them by the effective scale
   kind:
     :ordinal/:nominal/:point → distinct values (each gets its own slot)
     other (continuous) → [min max] via d3.extent"
  [channel data]
  (let [{:keys [field scale]} channel]
    (or (:domain scale)
        (let [values (field-values field data)]
          (case (scale-kind channel)
            (:ordinal :nominal :point) (vec (distinct values))
            (.extent d3 (clj->js values)))))))

(defn band-range-width
  [n bandwidth {:keys [padding-inner padding-outer inset]
                :or   {padding-inner 0 padding-outer 0}}]
  (+ (/ (* bandwidth (+ (- n padding-inner) (* 2 padding-outer)))
        (- 1 padding-inner))
     (:left inset 0)
     (:right inset 0)))

(defn- inset-range
  [[r0 r1] {:keys [left right] :or {left 0 right 0}}]
  [(+ r0 left) (- r1 right)])

(defn- mk-scale [{:keys [scale] :as channel} data range]
  (let [dom (domain channel data)]
    (-> channel
        scale-kind
        d3-scale
        (.domain (clj->js dom))
        (.range  (clj->js (cond-> (or (:range scale) range)
                            (:inset scale) (inset-range (:inset scale)))))
        (cond->
            (:nice scale)          (.nice)
            (:clamp scale)         (.clamp true)
            (:padding scale)       (.padding (:padding scale))
            (:padding-inner scale) (.paddingInner (:padding-inner scale))
            (:padding-outer scale) (.paddingOuter (:padding-outer scale))))))

(defn- mk-color-scale
  "Returns a {series-id color} map used by marks to pick a stroke per series.

   Two data shapes:
     - map      {:planned [...] :executed [...]} → series-ids = map keys
     - vector   [{:cat :a ...} {:cat :b ...}]    → series-ids = distinct
                                                    values of `field` (e.g. :cat)

   Colors come from the channel's :scale :range, paired by position with
   the series-ids. An explicit :scale :domain overrides the inferred ids."
  [{:keys [field scale]} data]
  (let [domain (or (:domain scale)
                   (cond
                     (map? data)    (vec (keys data))
                     (vector? data) (vec (distinct (map field data)))))]
    (zipmap domain (:range scale))))

(defn- stackable?
  "Returns true when the encoding will produce a stacked layout:
   a discrete color field layering over a quantitative Y."
  [encode]
  (and (#{:nominal :ordinal} (get-in encode [:color :type]))
       (= :quantitative (get-in encode [:y :type]))))

(defn- resolve-stack
  "Returns the effective stack mode for the Y channel.
   Mirrors Vega-Lite's resolution order:
     1. Explicit :stack on the Y encoding (including nil to opt out)
     2. :area always stacks when the encoding is stackable
     3. :bar only stacks when explicitly rendered stacked (:y :stacked),
        since a grouped/side-by-side bar mark is also `stackable?` but
        must NOT get a summed Y domain
     4. Otherwise nil"
  [{:keys [marks encode]}]
  (let [mark-type (get-in marks [0 :type])
        explicit  (get-in encode [:y :stack] ::not-set)]
    (cond
      (not= ::not-set explicit)                             explicit
      (and (= :area mark-type) (stackable? encode))         :zero
      (and (= :bar mark-type) (get-in encode [:y :stacked])) :zero
      :else                                                  nil)))

(defn- sums-per-x
  "Returns a seq of per-category sums of the measure field,
   used to compute the stacked Y domain."
  [data x-field measure-field]
  (->> data
       (group-by x-field)
       vals
       (map #(transduce (map measure-field) + 0 %))))

(defn- stacked-y-domain
  "Computes [0, max-stacked-sum] from flat data when stack-mode is :zero."
  [data encode]
  (let [x-field (get-in encode [:x :field])
        y-field (get-in encode [:y :field])
        hi      (apply max 0 (sums-per-x data x-field y-field))]
    [0 hi]))

(defn- bar-y-domain
  "Computes [lo hi] from flat, per-bar (non-summed) values, always
   including zero so a bar's height is measured from a zero baseline."
  [data y-field]
  (let [values (map y-field data)]
    [(apply min 0 values) (apply max 0 values)]))

(defn- resolve-y-domain
  "Returns the Y channel with :scale.domain filled in if it was absent.
   Explicit :scale.domain always wins."
  [y {:keys [marks encode]} data stack-mode]
  (cond
    (some? (get-in y [:scale :domain])) y
    (= stack-mode :zero)                (assoc-in y [:scale :domain] (stacked-y-domain data encode))
    (= :bar (get-in marks [0 :type]))   (assoc-in y [:scale :domain] (bar-y-domain data (get-in encode [:y :field])))
    :else                               y))

(defn- add-y-headroom
  "Extends a continuous Y scale's domain upward by one tick step."
  [y-scale tick-min-step]
  (if-let [step (->> [(axis/tick-step y-scale tick-min-step) tick-min-step]
                     (remove nil?)
                     seq
                     (apply max))]
    (let [[lo hi] (.domain y-scale)]
      (.domain y-scale #js [lo (+ hi step)]))
    y-scale))

(defn with-color-scale
  [{{:keys [color]} :encode :keys [data] :as ctx}]
  (cond-> ctx
    color (assoc-in [:encode :color :d3-scale] (mk-color-scale color data))))

(defn with-scales
  [{{:keys [x y]} :encode :keys [data width height] :as ctx}]
  (let [stack-mode      (resolve-stack ctx)
        explicit-y-dom? (some? (get-in y [:scale :domain]))
        y               (resolve-y-domain y ctx data stack-mode)
        y-scale         (cond-> (mk-scale y data [height 0])
                          (and (not explicit-y-dom?)
                               (not (#{:ordinal :nominal :point} (scale-kind y))))
                          (add-y-headroom (get-in y [:axis :tick-min-step])))]
    (-> ctx
        (assoc-in [:encode :x :d3-scale] (mk-scale x data [0 width]))
        (assoc-in [:encode :y :d3-scale] y-scale)
        with-color-scale)))
